// Bookie · Tuproq Guli — Housewarming variant that renders the standalone
// HTML template (Mediterranean-villa terracotta on cream, Fraunces
// display + Newsreader body + Manrope UI). Occasion is a family
// housewarming dinner, so `names` arrives as a single host-family
// string (e.g. "Karimov oilasi") rather than the ampersand-joined
// couple form. The HTML at /templates/bookie-terracotta.html is
// fully self-contained and we hand it the invitation payload through
// the iframe URL because postMessage would race the customizer's
// pre-paint text-node walk.
//
// No music toggle — this is an intimate family dinner, not a party.

import type { VariantProps } from "./types";
import { getLocale } from "@/lib/i18n";

const TEMPLATE_HTML = "/templates/bookie-terracotta.html";

export function BookieTerracottaStaticVariant({ t, ctx }: VariantProps) {
  const params = new URLSearchParams();
  if (t.names) params.set("names", t.names);
  if (t.date) {
    // Pass the original dd.mm.yyyy through — the customizer accepts
    // either dd.mm.yyyy or yyyy-mm-dd, but dd.mm.yyyy is what the
    // mock strings inside the HTML use, so the substitution loop
    // matches without a normalization round-trip.
    params.set("date", t.date);
  }
  if (t.venue) params.set("venue", t.venue);
  if (t.address) params.set("address", t.address);
  if (t.time) params.set("time", t.time);
  if (ctx.slug) params.set("slug", ctx.slug);
  // ?audio=/music/<id>.mp3 — owner-picked soundtrack from the
  // curated MusicSelector library. Empty/unset = silent card.
  if (t.musicTrackId) params.set("audio", "/music/" + t.musicTrackId + ".mp3");
  // Cache buster — bump when the static HTML changes.
  params.set("v", "3");

  // Demo/catalog preview (no real invitation slug): default the template's
  // in-iframe language to the app locale, so taklifla.com previews render in
  // English (and a RU viewer sees Russian). The HTMLs read ?lang= (all) and
  // ?default_lang= (newer premium ones) — set both. Owner-published
  // invitations (ctx.slug set) keep their own default_lang / built-in default.
  if (!ctx.slug) {
    const _loc = getLocale();
    if (!params.has("lang")) params.set("lang", _loc);
    if (!params.has("default_lang")) params.set("default_lang", _loc);
  }
  const src = `${TEMPLATE_HTML}?${params.toString()}`;

  return (
    <iframe
      src={src}
      title={t.names || "Karimov oilasi"}
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100svh",
        border: "none",
        display: "block",
        background: "#FAF3E3",
      }}
    />
  );
}
